import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar, Video, Clock, ArrowRight, ExternalLink,
  LayoutDashboard, Hash, AlertCircle, Loader2, Link2, Image
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { authFetch } from '../lib/api';
import VoicemailModule from './VoicemailModule';
import EmployeeDirectoryModule from './EmployeeDirectoryModule';

const API = process.env.REACT_APP_BACKEND_URL;

const DashboardHome = ({ user }) => {
  const [projects, setProjects] = useState([]);
  const [todaysMeetings, setTodaysMeetings] = useState([]);
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [mondayBoards, setMondayBoards] = useState([]);
  const [mondayConfigured, setMondayConfigured] = useState(false);
  const [newsFeed, setNewsFeed] = useState([]);
  const [newsConfigured, setNewsConfigured] = useState(false);
  const [newsError, setNewsError] = useState('');
  const [projectTab, setProjectTab] = useState('active');
  const [loading, setLoading] = useState(true);

  const userRoles = user.roles || [user.role];
  const hasClientAccess = userRoles.includes('Admin') || userRoles.includes('Client_Service');

  const fetchDashboardData = useCallback(async () => {
    try {
      const fetches = [
        authFetch(`${API}/api/projects`),
        authFetch(`${API}/api/monday/boards`),
        authFetch(`${API}/api/slack/newsfeed?per_channel=3`),
      ];
      if (hasClientAccess) {
        fetches.push(authFetch(`${API}/api/calendar/events/today`));
      }
      const results = await Promise.all(fetches);
      const [projectsRes, mondayRes, newsRes] = results;
      const calRes = hasClientAccess ? results[3] : null;

      if (projectsRes.ok) setProjects((await projectsRes.json()).projects || []);
      if (mondayRes.ok) { const d = await mondayRes.json(); setMondayBoards(d.boards || []); setMondayConfigured(d.configured); }
      if (newsRes.ok) { const d = await newsRes.json(); setNewsFeed(d.stories || []); setNewsConfigured(d.configured); if (d.errors) setNewsError(Object.values(d.errors).join(', ')); }
      if (calRes && calRes.ok) { const d = await calRes.json(); setTodaysMeetings(d.events || []); setCalendarConnected(d.connected !== false); }
    } catch (e) { toast.error('Failed to load dashboard data'); }
    finally { setLoading(false); }
  }, [hasClientAccess]);

  useEffect(() => { fetchDashboardData(); }, [fetchDashboardData]);

  const connectCalendar = async () => {
    try {
      const res = await authFetch(`${API}/api/calendar/auth/start`);
      if (res.ok) {
        const { authorization_url } = await res.json();
        window.open(authorization_url, '_blank', 'width=600,height=700');
        toast.info('Complete Google sign-in in the popup, then refresh.');
      }
    } catch (e) { toast.error('Failed to start calendar connection'); }
  };

  const activeProjects = projects.filter(p => p.status === 'Active');
  const recentProjects = [...projects].sort((a, b) =>
    new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at)
  ).slice(0, 5);
  const mainBoards = mondayBoards.filter(b => !b.name.toLowerCase().startsWith('subitems of')).slice(0, 8);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-brand" size={28} /></div>;
  }

  return (
    <div className="space-y-6" data-testid="dashboard-home">
      {/* Title */}
      <header>
        <h1 className="text-3xl font-semibold tracking-tight text-text-primary">
          Welcome back{user.name ? `, ${user.name.split(' ')[0]}` : ''}
        </h1>
        <p className="text-sm text-text-tertiary mt-1">
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
        </p>
      </header>

      {/* Live action modules — top of page (above the fold) */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <VoicemailModule />
        <EmployeeDirectoryModule />
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left column (3/5) */}
        <div className="lg:col-span-3 space-y-6">

          {/* Today's Meetings */}
          <section className="rounded-md border border-border-subtle bg-white shadow-xs" data-testid="todays-meetings">
            <div className="flex items-center justify-between border-b border-border-subtle px-5 py-4">
              <h3 className="text-sm font-semibold text-text-primary flex items-center gap-2">
                <Calendar size={16} className="text-brand" />
                Today's meetings
                {todaysMeetings.length > 0 && (
                  <span className="text-xs font-medium bg-brand/10 text-brand px-2 py-0.5 rounded-full">{todaysMeetings.length}</span>
                )}
              </h3>
              {hasClientAccess && (
                <Link to="/internal/client-management/meetings" className="text-xs font-medium text-brand hover:text-brand-hover flex items-center gap-1" data-testid="view-pulse">
                  The Pulse <ArrowRight size={12} />
                </Link>
              )}
            </div>
            <div className="p-5">
              {!calendarConnected && hasClientAccess ? (
                <div className="flex items-center justify-between py-3 px-4 bg-surface-page rounded-md border border-dashed border-border-strong">
                  <div>
                    <p className="text-sm font-medium text-text-primary">Connect Google Calendar</p>
                    <p className="text-xs text-text-tertiary">Link your calendar to see today's schedule</p>
                  </div>
                  <Button size="sm" onClick={connectCalendar} className="bg-brand text-white hover:bg-brand-hover shadow-xs" data-testid="connect-calendar-dash">
                    <Link2 size={14} className="mr-1.5" /> Connect
                  </Button>
                </div>
              ) : todaysMeetings.length === 0 ? (
                <p className="text-sm text-text-muted py-2">No meetings scheduled for today</p>
              ) : (
                <div className="space-y-2">
                  {todaysMeetings.map((ev, idx) => {
                    const startTime = ev.start?.dateTime ? new Date(ev.start.dateTime) : null;
                    const endTime = ev.end?.dateTime ? new Date(ev.end.dateTime) : null;
                    const hasGoogleMeet = !!(ev.hangoutLink || ev.conferenceData);
                    const now = new Date();
                    const isOngoing = startTime && endTime && now >= startTime && now <= endTime;
                    const isPast = endTime && now > endTime;

                    return (
                      <div key={ev.id || idx} className={`flex items-center gap-3 p-3 rounded-md transition-all duration-200 ease-productive ${
                        isOngoing ? 'bg-success/5 border border-success/20' :
                        isPast ? 'opacity-50 bg-surface-page' : 'bg-surface-page hover:bg-white'
                      }`} data-testid={`today-meeting-${idx}`}>
                        <div className={`w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 ${
                          isOngoing ? 'bg-success/10 text-success' :
                          hasGoogleMeet ? 'bg-info/10 text-info' : 'bg-surface-page text-text-muted'
                        }`}>
                          {hasGoogleMeet ? <Video size={16} /> : <Calendar size={16} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-text-primary text-sm truncate">{ev.summary || 'No Title'}</span>
                            {isOngoing && <span className="text-[10px] font-semibold uppercase tracking-wider text-success bg-success/10 px-1.5 py-0.5 rounded">Live</span>}
                            {isPast && <span className="text-[10px] font-semibold uppercase text-text-muted bg-surface-page px-1.5 py-0.5 rounded">Done</span>}
                          </div>
                          <span className="text-xs text-text-tertiary flex items-center gap-1 mt-0.5">
                            <Clock size={10} />
                            {startTime?.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                            {endTime ? ` – ${endTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}` : ''}
                          </span>
                        </div>
                        {hasGoogleMeet && !isPast && (
                          <a href={ev.hangoutLink || ev.conferenceData?.entryPoints?.[0]?.uri}
                            target="_blank" rel="noopener noreferrer"
                            className="text-xs font-medium text-info bg-info/10 hover:bg-info/20 px-3 py-1.5 rounded-md flex-shrink-0 transition-colors"
                            data-testid={`join-meeting-${idx}`}>
                            Join
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          {/* Monday.com Boards */}
          <section className="rounded-md border border-border-subtle bg-white shadow-xs" data-testid="monday-boards">
            <div className="flex items-center justify-between border-b border-border-subtle px-5 py-4">
              <h3 className="text-sm font-semibold text-text-primary flex items-center gap-2">
                <LayoutDashboard size={16} className="text-chart-purple" />
                Monday.com boards
                {mainBoards.length > 0 && (
                  <span className="text-xs font-medium bg-chart-purple/10 text-chart-purple px-2 py-0.5 rounded-full">{mainBoards.length}</span>
                )}
              </h3>
              {mondayConfigured && (
                <a href="https://bcm-teams.monday.com" target="_blank" rel="noopener noreferrer"
                  className="text-xs font-medium text-brand hover:text-brand-hover flex items-center gap-1">
                  Open Monday <ExternalLink size={12} />
                </a>
              )}
            </div>
            <div className="p-5">
              {!mondayConfigured ? (
                <div className="py-6 text-center">
                  <LayoutDashboard size={28} className="mx-auto text-text-muted mb-2" />
                  <p className="text-sm text-text-secondary">Monday.com not connected</p>
                </div>
              ) : mainBoards.length === 0 ? (
                <p className="text-sm text-text-muted py-2">No active boards found</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {mainBoards.map((board) => (
                    <a key={board.id}
                      href={`https://bcm-teams.monday.com/boards/${board.id}`}
                      target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 rounded-md bg-surface-page hover:bg-white hover:shadow-xs border border-transparent hover:border-border-subtle transition-all duration-200 ease-productive group"
                      data-testid={`monday-board-${board.id}`}>
                      <div className="w-9 h-9 rounded-md bg-chart-purple/10 text-chart-purple flex items-center justify-center flex-shrink-0">
                        <LayoutDashboard size={16} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-text-primary truncate group-hover:text-brand transition-colors">{board.name}</p>
                        <p className="text-xs text-text-tertiary">
                          {board.items_count || 0} items
                          {board.workspace?.name ? ` · ${board.workspace.name}` : ''}
                        </p>
                      </div>
                      <ExternalLink size={12} className="text-text-muted group-hover:text-brand flex-shrink-0 transition-colors" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>

        {/* Right column (2/5) */}
        <div className="lg:col-span-2 space-y-6">

          {/* Projects — toggle Active / Recent */}
          <section className="rounded-md border border-border-subtle bg-white shadow-xs" data-testid="projects-module">
            <div className="flex items-center justify-between border-b border-border-subtle px-5 py-4">
              <div className="flex items-center gap-1 bg-surface-page rounded-md p-0.5">
                <button
                  onClick={() => setProjectTab('active')}
                  className={`text-xs font-semibold px-3 py-1.5 rounded transition-all ${projectTab === 'active' ? 'bg-white text-brand shadow-xs' : 'text-text-muted hover:text-text-secondary'}`}
                  data-testid="projects-tab-active"
                >
                  Active
                  {activeProjects.length > 0 && <span className="ml-1 text-[10px] font-bold bg-success/10 text-success px-1.5 py-0.5 rounded-full">{activeProjects.length}</span>}
                </button>
                <button
                  onClick={() => setProjectTab('recent')}
                  className={`text-xs font-semibold px-3 py-1.5 rounded transition-all ${projectTab === 'recent' ? 'bg-white text-brand shadow-xs' : 'text-text-muted hover:text-text-secondary'}`}
                  data-testid="projects-tab-recent"
                >
                  Recent
                </button>
              </div>
              <Link to="/internal/projects" className="text-xs font-medium text-brand hover:text-brand-hover flex items-center gap-1" data-testid="view-all-projects">
                All projects <ArrowRight size={12} />
              </Link>
            </div>
            <div className="p-5">
              {projectTab === 'active' ? (
                activeProjects.length === 0 ? (
                  <p className="text-sm text-text-muted py-2">No active projects</p>
                ) : (
                  <div className="space-y-1">
                    {activeProjects.slice(0, 6).map((project) => (
                      <Link key={project.id} to={`/internal/projects/${project.id}`}
                        className="flex items-center gap-3 p-3 rounded-md hover:bg-surface-page transition-colors duration-200 ease-productive"
                        data-testid={`active-project-${project.id}`}>
                        <div className="w-2 h-2 rounded-full bg-success flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-text-primary truncate">{project.name}</p>
                          <p className="text-xs text-text-tertiary">{project.client_name || 'No client'}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                )
              ) : (
                recentProjects.length === 0 ? (
                  <p className="text-sm text-text-muted py-2">No projects yet</p>
                ) : (
                  <div className="space-y-1">
                    {recentProjects.map((project) => {
                      const updated = project.updated_at || project.created_at;
                      const date = updated ? new Date(updated) : null;
                      return (
                        <Link key={project.id} to={`/internal/projects/${project.id}`}
                          className="flex items-center gap-3 p-3 rounded-md hover:bg-surface-page transition-colors duration-200 ease-productive"
                          data-testid={`recent-project-${project.id}`}>
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                            project.status === 'Active' ? 'bg-success' :
                            project.status === 'Completed' ? 'bg-info' : 'bg-text-muted'
                          }`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-text-primary truncate">{project.name}</p>
                            <p className="text-xs text-text-tertiary">
                              {project.client_name || 'No client'}
                              {date ? ` · ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}
                            </p>
                          </div>
                          <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                            project.status === 'Active' ? 'text-success bg-success/10' :
                            project.status === 'Completed' ? 'text-info bg-info/10' : 'text-text-muted bg-surface-page'
                          }`}>{project.status}</span>
                        </Link>
                      );
                    })}
                  </div>
                )
              )}
            </div>
          </section>

          {/* News Feed (compact) */}
          <section className="rounded-md border border-border-subtle bg-white shadow-xs" data-testid="news-feed">
            <div className="flex items-center justify-between border-b border-border-subtle px-5 py-4">
              <h3 className="text-sm font-semibold text-text-primary flex items-center gap-2">
                <Hash size={16} className="text-brand" />
                News Feed
              </h3>
              <div className="flex items-center gap-1.5">
                <span className="text-[9px] font-semibold uppercase tracking-wider text-text-muted bg-surface-page px-1.5 py-0.5 rounded">#industry-news</span>
                <span className="text-[9px] font-semibold uppercase tracking-wider text-text-muted bg-surface-page px-1.5 py-0.5 rounded">#ai</span>
              </div>
            </div>
            <div className="p-4">
              {!newsConfigured ? (
                <div className="py-6 text-center">
                  <AlertCircle size={24} className="mx-auto text-warning mb-2" />
                  <p className="text-sm text-text-secondary font-medium">Slack bot token required</p>
                  <p className="text-xs text-text-tertiary mt-1">xoxb- token with channels:history scope</p>
                </div>
              ) : newsFeed.length === 0 ? (
                <p className="text-sm text-text-muted py-3 text-center">No recent stories</p>
              ) : (
                <div className="space-y-3">
                  {newsFeed.slice(0, 6).map((story, idx) => {
                    const ts = story.ts ? new Date(parseFloat(story.ts) * 1000) : null;
                    const channelLabel = story.channel === 'ai' ? '#ai' : '#news';
                    const channelColor = story.channel === 'ai' ? 'bg-chart-purple/10 text-chart-purple' : 'bg-brand/10 text-brand';

                    return (
                      <a
                        key={story.ts || idx}
                        href={story.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex gap-3 rounded-lg border border-border-subtle p-2.5 hover:border-brand/30 hover:shadow-sm transition-all duration-200"
                        data-testid={`news-card-${idx}`}
                      >
                        {/* Small thumbnail */}
                        <div className="relative w-20 h-16 rounded-md overflow-hidden bg-surface-page flex-shrink-0">
                          {story.image ? (
                            <img
                              src={story.image}
                              alt=""
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                            />
                          ) : null}
                          <div
                            className={`${story.image ? 'hidden' : 'flex'} absolute inset-0 items-center justify-center bg-gradient-to-br from-surface-page to-border-subtle`}
                          >
                            <Image size={18} className="text-text-muted/40" />
                          </div>
                        </div>
                        {/* Text content */}
                        <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
                          <h4 className="text-[13px] font-semibold text-text-primary line-clamp-2 leading-snug group-hover:text-brand transition-colors">
                            {story.title || story.slack_summary}
                          </h4>
                          <div className="flex items-center gap-1.5 mt-1">
                            <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${channelColor}`}>
                              {channelLabel}
                            </span>
                            {story.source && (
                              <span className="text-[10px] text-text-tertiary truncate max-w-[100px]">{story.source}</span>
                            )}
                            {ts && (
                              <span className="text-[10px] text-text-muted">
                                {ts.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </span>
                            )}
                            <ExternalLink size={10} className="text-text-muted group-hover:text-brand ml-auto flex-shrink-0 transition-colors" />
                          </div>
                        </div>
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
